import { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "motion/react"
import { Excellent } from "./svgCollection"

const DISPLAY = "'Tilda Sans', sans-serif"
const BODY = "'Manrope', sans-serif"
const MONO = "'JetBrains Mono', monospace"
const SHEET_BG = "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)"

type Phase = "confirm" | "loading" | "success"

type OpenOptions = {
    orderId?: string
    unpaid?: boolean
    onCancel: () => Promise<void>
    onSuccess?: () => void
}

export type CancelOrderDialogHandle = {
    open: (options: OpenOptions) => void
    close: () => void
}

function PopcornMark({ animated = false }: { animated?: boolean }) {
    return (
        <img
            src={require("../assets/pop-corn.gif")}
            alt=""
            className={`size-[72px] object-contain ${animated ? "opacity-90" : "opacity-80"}`}
        />
    )
}

const CancelOrderDialog = forwardRef<CancelOrderDialogHandle>((_, ref) => {
    const [open, setOpen] = useState(false)
    const [phase, setPhase] = useState<Phase>("confirm")
    const [unpaid, setUnpaid] = useState(false)
    const optionsRef = useRef<OpenOptions | null>(null)

    const close = () => {
        setOpen(false)
        optionsRef.current = null
        window.setTimeout(() => setPhase("confirm"), 200)
    }

    useImperativeHandle(ref, () => ({
        open(options) {
            optionsRef.current = options
            setUnpaid(options.unpaid ?? false)
            setPhase("confirm")
            setOpen(true)
        },
        close,
    }))

    const handleConfirm = async () => {
        const options = optionsRef.current
        if (!options) return

        setPhase("loading")
        try {
            await options.onCancel()
            setPhase("success")
        } catch {
            setPhase("confirm")
        }
    }

    const handleDone = () => {
        const onSuccess = optionsRef.current?.onSuccess
        close()
        onSuccess?.()
    }

    const panel = (
        <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="flex w-full max-h-[92svh] flex-col overflow-y-auto rounded-t-[28px] border border-transparent px-5 py-8 pb-[max(1.5rem,var(--safe-bottom))] md:max-w-[400px] md:rounded-[24px] md:border-white/10 md:py-10 md:shadow-[0_24px_64px_rgba(0,0,0,0.5)]"
            style={{
                background: SHEET_BG,
                borderTop: "1.5px solid rgba(255,255,255,0.12)",
                boxShadow: "0 -8px 40px rgba(0,0,0,0.35)",
            }}
            onClick={e => e.stopPropagation()}>
            {phase === "confirm" && (
                <div className="flex flex-col items-center gap-6 text-center">
                    <PopcornMark />
                    <div className="flex max-w-[320px] flex-col gap-3">
                        <h2
                            className="text-[20px] leading-snug text-white md:text-[22px]"
                            style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: "-0.02em" }}>
                            Вы уверены, что хотите отменить билет?
                        </h2>
                        <p className="text-[14px] leading-relaxed text-white/55 md:text-[15px]" style={{ fontFamily: BODY, fontWeight: 500 }}>
                            {unpaid
                                ? "Заказ будет удалён, места освободятся. Это действие нельзя будет отменить."
                                : "Это действие нельзя будет отменить. Полученные баллы сгорят, а списанные вернутся на счёт."}
                        </p>
                    </div>
                    <div className="flex w-full max-w-[340px] flex-col gap-2.5 pt-2">
                        <button
                            type="button"
                            onClick={handleConfirm}
                            className="h-[50px] w-full cursor-pointer rounded-[14px] bg-white/10 text-[15px] text-white transition hover:bg-white/15"
                            style={{ fontFamily: BODY, fontWeight: 700 }}>
                            Да, отмените пожалуйста
                        </button>
                        <button
                            type="button"
                            onClick={close}
                            className="h-[50px] w-full cursor-pointer rounded-[14px] text-[15px] text-white"
                            style={{
                                fontFamily: BODY,
                                fontWeight: 700,
                                background: "#349be6",
                                boxShadow: "0 0 18px rgba(52,155,230,0.35)",
                            }}>
                            Нет, я нажал сюда случайно
                        </button>
                    </div>
                </div>
            )}

            {phase === "loading" && (
                <div className="flex min-h-[280px] flex-col items-center justify-center gap-5 px-2 text-center md:min-h-[300px]">
                    <PopcornMark animated />
                    <div className="flex flex-col gap-2">
                        <p className="text-[20px] leading-snug text-white md:text-[22px]" style={{ fontFamily: BODY, fontWeight: 700 }}>
                            Отменяем билет...
                        </p>
                        <p className="text-[14px] leading-relaxed text-white/50 md:text-[15px]" style={{ fontFamily: BODY, fontWeight: 500 }}>
                            Это может занять некоторое время.
                            <br />
                            Пожалуйста, не закрывайте страницу
                        </p>
                    </div>
                </div>
            )}

            {phase === "success" && (
                <div className="flex min-h-[280px] flex-col items-center justify-center gap-6 px-2 text-center md:min-h-[300px]">
                    <Excellent />
                    <div className="flex max-w-[320px] flex-col gap-3">
                        <p className="text-[20px] leading-snug text-white md:text-[22px]" style={{ fontFamily: BODY, fontWeight: 700 }}>
                            Билет успешно отменён
                        </p>
                        {!unpaid && (
                            <p className="text-[14px] leading-relaxed text-white/50 md:text-[15px]" style={{ fontFamily: BODY, fontWeight: 500 }}>
                                Срок возврата средств:
                                <br />— Банковская карта: до 10 банковских дней
                                <br />— СБП: до 24 часов
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleDone}
                        className="mt-2 h-[50px] w-full max-w-[340px] cursor-pointer rounded-[14px] text-[16px] text-white"
                        style={{
                            background: "#349be6",
                            fontFamily: MONO,
                            fontWeight: 700,
                            boxShadow: "0 0 18px rgba(52,155,230,0.35)",
                        }}>
                        Хорошо
                    </button>
                </div>
            )}
        </motion.div>
    )

    if (typeof document === "undefined") return null

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    key="cancel-order-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[260] flex cursor-pointer items-end justify-center md:items-center md:px-4"
                    style={{ background: "rgba(10,18,42,0.75)", backdropFilter: "blur(6px)" }}
                    onClick={phase === "loading" ? undefined : close}>
                    {panel}
                </motion.div>
            )}
        </AnimatePresence>,
        document.body,
    )
})

CancelOrderDialog.displayName = "CancelOrderDialog"

export default CancelOrderDialog
